import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Snackbar,
  Typography,
  Paper,
  alpha,
  Stack,
  useTheme
} from "@mui/material";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import {
  Edit as EditIcon,
  Dashboard as DashboardIcon,
  Info as InfoIcon
} from "@mui/icons-material";
import { tokens } from "../../theme";
import { siteCmsUrls } from "../../api/siteCms";

const AboutUsView = () => {
  const theme = useTheme();
  const baseColors = tokens(theme.palette.mode);

  // Using the same color scheme as in the Dashboard component
  const colors = {
    ...baseColors,
    blueAccent: {
      100: "#d9eefb",
      200: "#b3ddf7",
      300: "#8dcbf4",
      400: "#67baf0",
      500: "#22a3f1",
      600: "#1b82c1",
      700: "#146291",
      800: "#0e4160",
      900: "#072030",
    },
  };

  const { id } = useParams();
  const [aboutData, setAboutData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAboutData = async () => {
      try {
        const response = await axios.get(siteCmsUrls.about);
        if (response.data.status) {
          setAboutData(response.data.data);
        } else {
          throw new Error(response.data.message);
        }
      } catch (err) {
        setError(err);
        setSnackbarMessage("Error fetching About Us data");
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    };

    fetchAboutData();
  }, [id]);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleEditClick = () => {
    navigate(`/about-us/edit`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m="20px" height="80vh" overflow="auto" paddingRight="20px">
      {/* Header Section - Styled similarly to the Dashboard header */}
      <Box
        mb={3}
        p={3}
        borderRadius={2}
        sx={{
          background: `linear-gradient(90deg, ${colors.blueAccent[500]} 0%, ${colors.blueAccent[400]} 100%)`,
          boxShadow: `0 4px 12px rgba(0, 0, 0, 0.1)`,
          border: `1px solid rgba(255, 255, 255, 0.3)`,
        }}
      >
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <DashboardIcon sx={{ color: "white", fontSize: 28 }} />
          <Typography variant="h3" fontWeight="bold" color="white">
            About Us
          </Typography>
        </Box>
        <Typography variant="body1" color="white">
          Manage your company information and details
        </Typography>
      </Box>

      {/* Content Section */}
      {aboutData && (
        <Grid container spacing={3}>
          {/* Titles Section */}
          <Grid item xs={12}>
            <Paper
              sx={{
                p: 3,
                backgroundColor: "#ffffff",
                boxShadow: `0 10px 30px rgba(0, 0, 0, 0.05)`,
                border: `1px solid #e0e0e0`,
                borderRadius: 2,
                transition: "transform 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-4px)",
                  backgroundColor: "#ffffff",
                },
              }}
            >
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box
                  sx={{
                    backgroundColor: alpha(colors.blueAccent[500], 0.2),
                    borderRadius: "8px",
                    p: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <InfoIcon
                    sx={{
                      color: colors.blueAccent[500],
                      fontSize: 24,
                    }}
                  />
                </Box>
                <Box>
                  <Typography
                    variant="h5"
                    fontWeight="bold"
                    color="#333333"
                    mb={1}
                  >
                    {aboutData.title1}
                  </Typography>
                  <Typography
                    variant="h6"
                    color="#555555"
                    mb={2}
                  >
                    {aboutData.title2}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="#666666"
                    sx={{ whiteSpace: 'pre-wrap' }}
                  >
                    {aboutData.description}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          {/* Image Section */}
          <Grid item xs={12}>
            <Paper
              sx={{
                p: 3,
                backgroundColor: "#ffffff",
                boxShadow: `0 10px 30px rgba(0, 0, 0, 0.05)`,
                border: `1px solid #e0e0e0`,
                borderRadius: 2,
                transition: "transform 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-4px)",
                  backgroundColor: "#ffffff",
                },
              }}
            >
              <Typography
                variant="h5"
                fontWeight="bold"
                color="#333333"
                mb={2}
              >
                About Us Image
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: 2,
                  border: `1px dashed ${alpha(colors.blueAccent[500], 0.5)}`,
                  borderRadius: 1,
                  backgroundColor: alpha(colors.blueAccent[100], 0.1),
                }}
              >
                <img
                  src={aboutData.imageURL}
                  alt="About Us"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '400px',
                    objectFit: 'contain',
                  }}
                />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Edit Button */}
      <Box mt={3} display="flex" justifyContent="center">
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={handleEditClick}
          sx={{
            backgroundColor: colors.blueAccent[500],
            color: "white",
            fontSize: "14px",
            padding: "10px 20px",
            "&:hover": {
              backgroundColor: colors.blueAccent[600],
            },
          }}
        >
          Edit About Us Details
        </Button>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default AboutUsView;